import React, { Component } from 'react';
import { Button, DatePicker, Drawer, Empty, message, Pagination, Select, Skeleton, Spin, Switch, Tooltip } from 'antd';
import moment from 'moment';
import qs from 'qs';
import * as XLSX from 'xlsx';

import { fetchData } from '../../../../lib/apis';
import './revenue.css';

const { RangePicker } = DatePicker;

const EMPTY_DATA = {
    metrics: {
        delivered_revenue: 0,
        confirmed_revenue: 0,
        delivered_orders: 0,
        average_order_value: 0,
        cancel_return_value: 0,
        comparisons: null
    },
    trend: [],
    levels: [],
    options: { levels: [], employees: [], sources: [], products: [], statuses: [] },
    data_quality: {},
    updated_at: 0
};

const currencyFormatter = new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0
});

const numberFormatter = new Intl.NumberFormat('vi-VN');

function formatCurrency(value) {
    return currencyFormatter.format(Number(value || 0)).replace('₫', '₫');
}

function formatNumber(value) {
    return numberFormatter.format(Number(value || 0));
}

function getApiErrorMessage(error, fallback) {
    if (error && typeof error.message === 'string' && error.message.trim()) return error.message;
    if (error && error.errors && typeof error.errors.message === 'string' && error.errors.message.trim()) return error.errors.message;
    if (error && error.err && typeof error.err.message === 'string' && error.err.message.trim()) return error.err.message;
    return fallback;
}

function Comparison({ value, inverse }) {
    if (value === null || typeof value === 'undefined') {
        return <div className="revenue-kpi__delta revenue-kpi__delta--muted">Chưa có dữ liệu kỳ trước</div>;
    }
    const positive = inverse ? value <= 0 : value >= 0;
    const icon = value > 0 ? 'fa-arrow-up' : value < 0 ? 'fa-arrow-down' : 'fa-minus';
    return (
        <div className={`revenue-kpi__delta ${positive ? 'revenue-kpi__delta--positive' : 'revenue-kpi__delta--negative'}`}>
            <i className={`fas ${icon}`} aria-hidden="true"></i>
            {Math.abs(value).toLocaleString('vi-VN')}%
            <span>vs kỳ trước</span>
        </div>
    );
}

function TrendChart({ series }) {
    if (!series || !series.some((item) => item.delivered || item.confirmed)) {
        return <div className="revenue-chart-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có doanh thu trong kỳ" /></div>;
    }
    const width = Math.max(700, series.length * 38);
    const height = 242;
    const top = 12;
    const bottom = 34;
    const plotHeight = height - top - bottom;
    const maxValue = Math.max.apply(null, series.reduce((values, item) => values.concat([item.delivered || 0, item.confirmed || 0]), [1]));
    const step = width / series.length;
    const barWidth = Math.max(4, Math.min(12, (step - 8) / 2));
    const shouldShowLabel = (index) => series.length <= 10 || index === 0 || index === series.length - 1 || index % Math.ceil(series.length / 7) === 0;

    return (
        <div className="revenue-trend-scroll">
            <svg className="revenue-trend" viewBox={`0 0 ${width} ${height}`} style={{ minWidth: width }} role="img" aria-label="Xu hướng doanh thu giao thành công và đã xác nhận">
                {[0, 0.25, 0.5, 0.75, 1].map((part) => {
                    const y = top + plotHeight - (part * plotHeight);
                    return <line key={part} x1="0" y1={y} x2={width} y2={y} className="revenue-trend__grid" />;
                })}
                {series.map((item, index) => {
                    const deliveredHeight = Math.max(item.delivered ? 2 : 0, (item.delivered / maxValue) * plotHeight);
                    const confirmedHeight = Math.max(item.confirmed ? 2 : 0, (item.confirmed / maxValue) * plotHeight);
                    const center = step * index + step / 2;
                    return (
                        <g key={item.date || index}>
                            <Tooltip title={`Đã xác nhận: ${formatCurrency(item.confirmed)}`}>
                                <rect x={center - barWidth - 1} y={top + plotHeight - confirmedHeight} width={barWidth} height={confirmedHeight} rx="2" className="revenue-trend__confirmed" />
                            </Tooltip>
                            <Tooltip title={`Giao thành công: ${formatCurrency(item.delivered)}`}>
                                <rect x={center + 1} y={top + plotHeight - deliveredHeight} width={barWidth} height={deliveredHeight} rx="2" className="revenue-trend__delivered" />
                            </Tooltip>
                            {shouldShowLabel(index) && <text x={center} y={height - 9} textAnchor="middle" className="revenue-trend__label">{item.label}</text>}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

export default class RevenueReportView extends Component {
    constructor(props) {
        super(props);
        const dateRange = [moment().startOf('month'), moment().endOf('day')];
        this.state = {
            loading: true,
            error: false,
            data: EMPTY_DATA,
            filters: {
                dateRange,
                basis: 'delivered',
                level: undefined,
                sale_id: undefined,
                channel_type: undefined,
                product_id: undefined,
                status: undefined,
                compare_previous: true
            },
            appliedFilters: null,
            ordersOpen: false,
            ordersLoading: false,
            orders: [],
            ordersTotal: 0,
            ordersPage: 1,
            selectedLevel: null
        };
    }

    componentDidMount() {
        this.applyFilters();
    }

    componentDidUpdate(prevProps) {
        const previousCompany = prevProps.companySelected && prevProps.companySelected.company_id;
        const currentCompany = this.props.companySelected && this.props.companySelected.company_id;
        if (previousCompany !== currentCompany) this.applyFilters();
    }

    buildParams = (filters, extra = {}) => ({
        time_start: filters.dateRange[0].startOf('day').valueOf(),
        time_end: filters.dateRange[1].endOf('day').valueOf(),
        basis: filters.basis,
        level: filters.level || undefined,
        sale_id: filters.sale_id || undefined,
        channel_type: filters.channel_type || undefined,
        product_id: filters.product_id || undefined,
        status: filters.status || undefined,
        compare_previous: filters.compare_previous ? 1 : 0,
        ...extra
    });

    loadReport = async (filters) => {
        this.setState({ loading: true, error: false });
        try {
            const response = await fetchData({
                url: `api/v1/reports/revenue?${qs.stringify(this.buildParams(filters), { skipNulls: true })}`
            });
            this.setState({
                loading: false,
                error: false,
                data: { ...EMPTY_DATA, ...(response.data || {}) },
                appliedFilters: { ...filters, dateRange: [...filters.dateRange] }
            });
        } catch (err) {
            this.setState({ loading: false, error: true });
            message.error(getApiErrorMessage(err, 'Không tải được báo cáo doanh thu. Vui lòng thử lại.'));
        }
    };

    applyFilters = () => {
        const { filters } = this.state;
        if (!filters.dateRange || filters.dateRange.length !== 2) {
            message.warning('Vui lòng chọn khoảng thời gian báo cáo.');
            return;
        }
        this.loadReport(filters);
    };

    resetFilters = () => {
        const filters = {
            dateRange: [moment().startOf('month'), moment().endOf('day')],
            basis: 'delivered',
            level: undefined,
            sale_id: undefined,
            channel_type: undefined,
            product_id: undefined,
            status: undefined,
            compare_previous: true
        };
        this.setState({ filters }, () => this.loadReport(filters));
    };

    updateFilter = (key, value) => {
        this.setState((state) => ({ filters: { ...state.filters, [key]: value } }));
    };

    exportExcel = () => {
        const { data, appliedFilters } = this.state;
        if (!appliedFilters) return;
        const rows = (data.levels || []).map((item) => ({
            Level: item.label,
            'Doanh thu': item.revenue,
            'Tỷ trọng (%)': item.share,
            'Số đơn': item.order_count,
            'Giá trị trung bình': item.average_order_value,
            'Giao thành công (%)': item.delivery_success_rate,
            'Hủy / hoàn (%)': item.cancel_return_rate,
            'So kỳ trước (%)': item.comparison
        }));
        const summary = [
            ['BÁO CÁO DOANH THU'],
            ['Từ ngày', appliedFilters.dateRange[0].format('DD/MM/YYYY'), 'Đến ngày', appliedFilters.dateRange[1].format('DD/MM/YYYY')],
            ['Doanh thu giao thành công', data.metrics.delivered_revenue],
            ['Doanh thu đã xác nhận', data.metrics.confirmed_revenue],
            ['Đơn giao thành công', data.metrics.delivered_orders],
            ['Giá trị đơn trung bình', data.metrics.average_order_value],
            ['Giá trị hủy & hoàn', data.metrics.cancel_return_value]
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summary), 'Tổng quan');
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Theo level');
        XLSX.writeFile(workbook, `bao-cao-doanh-thu-${moment().format('YYYYMMDD-HHmm')}.xlsx`);
    };

    openOrders = (level, page = 1) => {
        this.setState({ ordersOpen: true, selectedLevel: level, ordersPage: page }, () => this.loadOrders(page));
    };

    loadOrders = async (page = 1) => {
        const { appliedFilters, selectedLevel } = this.state;
        if (!appliedFilters || !selectedLevel) return;
        this.setState({ ordersLoading: true });
        try {
            const params = this.buildParams(appliedFilters, {
                level: undefined,
                detail_level: selectedLevel.key,
                include_orders: 1,
                page,
                limit: 20
            });
            const response = await fetchData({ url: `api/v1/reports/revenue?${qs.stringify(params, { skipNulls: true })}` });
            const orders = (response.data && response.data.orders) || {};
            this.setState({
                ordersLoading: false,
                orders: orders.items || [],
                ordersTotal: orders.total || 0,
                ordersPage: orders.page || page
            });
        } catch (err) {
            this.setState({ ordersLoading: false });
            message.error(getApiErrorMessage(err, 'Không tải được danh sách đơn hàng.'));
        }
    };

    renderFilters() {
        const { filters, data } = this.state;
        const options = data.options || EMPTY_DATA.options;
        return (
            <section className="revenue-filter" aria-label="Bộ lọc báo cáo">
                <div className="revenue-filter__grid">
                    <div className="revenue-field revenue-field--date">
                        <label>Thời gian</label>
                        <RangePicker
                            value={filters.dateRange}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            onChange={(value) => this.updateFilter('dateRange', value)}
                            ranges={{
                                '7 ngày gần nhất': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
                                'Tháng này': [moment().startOf('month'), moment().endOf('day')],
                                'Tháng trước': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
                            }}
                            suffixIcon={<i className="fal fa-calendar-alt" />}
                        />
                    </div>
                    <div className="revenue-field">
                        <label>Cơ sở ghi nhận</label>
                        <div className="revenue-basis" role="group" aria-label="Cơ sở ghi nhận doanh thu">
                            <button type="button" className={filters.basis === 'delivered' ? 'active' : ''} onClick={() => this.updateFilter('basis', 'delivered')}>Giao thành công</button>
                            <button type="button" className={filters.basis === 'confirmed' ? 'active' : ''} onClick={() => this.updateFilter('basis', 'confirmed')}>Đã xác nhận</button>
                        </div>
                    </div>
                    <ReportSelect label="Level" placeholder="Tất cả Level" value={filters.level} options={options.levels} onChange={(value) => this.updateFilter('level', value)} />
                    <ReportSelect label="Nhân viên" placeholder="Tất cả nhân viên" value={filters.sale_id} options={(options.employees || []).filter((item) => !filters.level || item.level === filters.level)} onChange={(value) => this.updateFilter('sale_id', value)} showSearch />
                    <ReportSelect label="Nguồn / kênh" placeholder="Tất cả nguồn" value={filters.channel_type} options={options.sources} onChange={(value) => this.updateFilter('channel_type', value)} />
                    <ReportSelect label="Sản phẩm" placeholder="Tất cả sản phẩm" value={filters.product_id} options={options.products} onChange={(value) => this.updateFilter('product_id', value)} showSearch />
                    <ReportSelect label="Trạng thái" placeholder="Tất cả trạng thái" value={filters.status} options={options.statuses} onChange={(value) => this.updateFilter('status', value)} />
                </div>
                <div className="revenue-filter__footer">
                    <label className="revenue-compare">
                        <Switch size="small" checked={filters.compare_previous} onChange={(checked) => this.updateFilter('compare_previous', checked)} />
                        <span>So sánh kỳ trước</span>
                    </label>
                    <div className="revenue-filter__actions">
                        <Button type="text" onClick={this.resetFilters}>Thiết lập lại</Button>
                        <Button type="primary" onClick={this.applyFilters} loading={this.state.loading}>Áp dụng</Button>
                    </div>
                </div>
            </section>
        );
    }

    renderKpis() {
        const { metrics } = this.state.data;
        const comparisons = metrics.comparisons || {};
        const cards = [
            { key: 'delivered_revenue', label: 'Doanh thu giao thành công', value: formatCurrency(metrics.delivered_revenue) },
            { key: 'confirmed_revenue', label: 'Doanh thu đã xác nhận', value: formatCurrency(metrics.confirmed_revenue) },
            { key: 'delivered_orders', label: 'Đơn giao thành công', value: formatNumber(metrics.delivered_orders) },
            { key: 'average_order_value', label: 'Giá trị đơn trung bình', value: formatCurrency(metrics.average_order_value) },
            { key: 'cancel_return_value', label: 'Hủy & hoàn', value: formatCurrency(metrics.cancel_return_value), inverse: true }
        ];
        return (
            <section className="revenue-kpis" aria-label="Chỉ số doanh thu">
                {cards.map((card) => (
                    <article className="revenue-kpi" key={card.key}>
                        <div className="revenue-kpi__label">{card.label}</div>
                        <div className="revenue-kpi__value">{card.value}</div>
                        {metrics.comparisons
                            ? <Comparison value={comparisons[card.key]} inverse={card.inverse} />
                            : <div className="revenue-kpi__delta revenue-kpi__delta--muted">Không so sánh kỳ trước</div>}
                    </article>
                ))}
            </section>
        );
    }

    renderLevelRanking() {
        const levels = (this.state.data.levels || []).filter((item) => item.revenue > 0);
        return (
            <section className="revenue-panel revenue-level-chart">
                <h2>Doanh thu theo level</h2>
                <p>Phân bổ giá trị {this.state.data.basis === 'confirmed' ? 'đã xác nhận' : 'giao thành công'}</p>
                {levels.length ? (
                    <div className="revenue-level-chart__list">
                        {levels.map((item) => (
                            <div className="revenue-level-chart__item" key={item.key}>
                                <div className="revenue-level-chart__meta">
                                    <strong>{item.label}</strong>
                                    <span>{item.share}% <b>•</b> {formatCurrency(item.revenue)}</span>
                                </div>
                                <div className="revenue-level-chart__track"><span style={{ width: `${Math.max(item.share, 1)}%`, backgroundColor: item.color }} /></div>
                            </div>
                        ))}
                    </div>
                ) : <div className="revenue-chart-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dữ liệu theo level" /></div>}
            </section>
        );
    }

    renderLevelTable() {
        const levels = this.state.data.levels || [];
        return (
            <section className="revenue-table-panel">
                <div className="revenue-table-panel__heading"><h2>Hiệu suất theo level</h2></div>
                <div className="revenue-table-scroll">
                    <table className="revenue-level-table">
                        <thead><tr>
                            <th aria-label="Mở chi tiết"></th><th>Level</th><th>Doanh thu</th><th>Tỷ trọng</th><th>Số đơn</th><th>Giá trị TB</th><th>Giao thành công</th><th>Hủy / hoàn</th><th>So kỳ trước</th><th></th>
                        </tr></thead>
                        <tbody>
                            {levels.map((item) => (
                                <tr key={item.key} onClick={() => this.openOrders(item)}>
                                    <td><i className="fas fa-chevron-right revenue-level-table__chevron" /></td>
                                    <td className="revenue-level-table__level">{item.label}</td>
                                    <td className="number">{formatCurrency(item.revenue)}</td>
                                    <td className="number">{item.share}%</td>
                                    <td className="number">{formatNumber(item.order_count)}</td>
                                    <td className="number">{formatCurrency(item.average_order_value)}</td>
                                    <td className="center"><span className="revenue-rate revenue-rate--success">{item.delivery_success_rate}%</span></td>
                                    <td className="center"><span className={`revenue-rate ${item.cancel_return_rate >= 8 ? 'revenue-rate--warning' : 'revenue-rate--danger'}`}>{item.cancel_return_rate}%</span></td>
                                    <td className={`number revenue-comparison ${item.comparison !== null && item.comparison >= 0 ? 'positive' : ''}`}>{item.comparison === null ? '—' : `${item.comparison >= 0 ? '+' : ''}${item.comparison}%`}</td>
                                    <td><button type="button" onClick={(event) => { event.stopPropagation(); this.openOrders(item); }}>Xem đơn hàng</button></td>
                                </tr>
                            ))}
                            {!levels.length && <tr><td colSpan="10"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dữ liệu theo level" /></td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    }

    renderOrdersDrawer() {
        const { selectedLevel, ordersOpen, orders, ordersLoading, ordersPage, ordersTotal } = this.state;
        return (
            <Drawer
                title={`Đơn hàng • ${(selectedLevel && selectedLevel.label) || ''}`}
                width={Math.min(980, window.innerWidth - 24)}
                visible={ordersOpen}
                onClose={() => this.setState({ ordersOpen: false })}
                destroyOnClose
            >
                <Spin spinning={ordersLoading}>
                    <div className="revenue-orders-table-scroll">
                        <table className="revenue-orders-table">
                            <thead><tr>
                                <th>Mã đơn</th><th>Khách hàng</th><th>Nhân viên</th><th>Trạng thái</th><th>Ngày ghi nhận</th><th>Doanh thu</th>
                            </tr></thead>
                            <tbody>
                                {orders.map((item) => (
                                    <tr key={item._id}>
                                        <td><a href={`/order/${item._id}`}>{item.order_code}</a></td>
                                        <td>{item.customer_name || '—'}</td>
                                        <td>{item.assignee_name || 'Chưa gán'}</td>
                                        <td>{item.status_label || '—'}</td>
                                        <td>{item.basis_at ? moment(item.basis_at).format('DD/MM/YYYY HH:mm') : '—'}</td>
                                        <td className="number">{formatCurrency(item.revenue)}</td>
                                    </tr>
                                ))}
                                {!ordersLoading && !orders.length && (
                                    <tr><td colSpan="6"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có đơn hàng phù hợp" /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {ordersTotal > 20 && (
                        <div className="revenue-orders-pagination">
                            <Pagination
                                current={ordersPage}
                                total={ordersTotal}
                                pageSize={20}
                                showSizeChanger={false}
                                onChange={(page) => this.openOrders(selectedLevel, page)}
                            />
                        </div>
                    )}
                </Spin>
            </Drawer>
        );
    }

    render() {
        const { data, loading, error, appliedFilters } = this.state;
        return (
            <main className="revenue-report">
                <div className="revenue-report__inner">
                    <header className="revenue-heading">
                        <div>
                            <p className="revenue-heading__eyebrow">Bán hàng / Báo cáo</p>
                            <h1>Báo cáo doanh thu</h1>
                            <p className="revenue-heading__description">
                                Mặc định hiển thị doanh thu theo <strong>Đơn giao thành công</strong>.
                                {data.updated_at ? ` Cập nhật lúc ${moment(data.updated_at).format('HH:mm, DD/MM/YYYY')}.` : ''}
                            </p>
                        </div>
                        <Button icon={<i className="fal fa-file-excel" />} onClick={this.exportExcel} disabled={!appliedFilters || loading}>Xuất Excel</Button>
                    </header>

                    {this.renderFilters()}
                    {error && <div className="revenue-alert"><span>Chưa tải được số liệu mới nhất.</span><button type="button" onClick={this.applyFilters}>Thử lại</button></div>}

                    <Spin spinning={loading && !!appliedFilters}>
                        {loading && !appliedFilters ? <Skeleton active paragraph={{ rows: 12 }} /> : (
                            <React.Fragment>
                                {this.renderKpis()}
                                <div className="revenue-charts">
                                    <section className="revenue-panel revenue-trend-panel">
                                        <div className="revenue-panel__heading">
                                            <div><h2>Xu hướng doanh thu</h2><p>Diễn biến theo ngày trong kỳ</p></div>
                                            <div className="revenue-legend"><span><i className="delivered" />Giao thành công</span><span><i className="confirmed" />Đã xác nhận</span></div>
                                        </div>
                                        <TrendChart series={data.trend || []} />
                                    </section>
                                    {this.renderLevelRanking()}
                                </div>
                                {this.renderLevelTable()}
                                <div className={`revenue-definition ${data.data_quality && data.data_quality.uses_delivery_time_fallback ? 'revenue-definition--warning' : ''}`}>
                                    <i className="fal fa-info-circle" />
                                    <p>Doanh thu thực hiện = tổng giá trị đơn giao thành công trong kỳ. Doanh thu xác nhận được theo dõi riêng; chưa phải tiền thực thu. {data.data_quality && data.data_quality.note}</p>
                                </div>
                            </React.Fragment>
                        )}
                    </Spin>
                </div>
                {this.renderOrdersDrawer()}
            </main>
        );
    }
}

function ReportSelect({ label, placeholder, value, options, onChange, showSearch }) {
    return (
        <div className="revenue-field">
            <label>{label}</label>
            <Select
                value={value}
                placeholder={placeholder}
                allowClear
                showSearch={showSearch}
                optionFilterProp="children"
                onChange={onChange}
                dropdownMatchSelectWidth={false}
            >
                {(options || []).map((item) => <Select.Option value={item.value} key={item.value}>{item.label}</Select.Option>)}
            </Select>
        </div>
    );
}
